import React from 'react'
import {
  Tooltip,
  type TooltipProps,
  Typography,
  type TypographyProps,
} from '@mui/material'

/**
 * `createElement` shims for the `.ts` exception renderers (no JSX available).
 *
 * Both mirror `React.createElement(Component, props, ...children)` argument
 * order so call sites change by only dropping the component-name line.
 */

/**
 * Wrap a single element in an MUI Tooltip.
 *
 * `createElement(Tooltip, props, child)` passes the child positionally, but
 * `TooltipProps` requires `children`, so `tsc` rejects the props object. Moving
 * the child into the props object satisfies the type and is identical at runtime.
 */
export const withTooltip = (
  props: React.Attributes & Omit<TooltipProps, 'children'>,
  child: React.ReactElement,
): React.ReactElement => React.createElement(Tooltip, { ...props, children: child })

/**
 * Build a Typography element. `createElement(Typography, { component })` fails
 * because `component` is omitted from the props type `createElement` infers for
 * an OverridableComponent; typing the props as `TypographyProps` restores it.
 */
export const typographyEl = (
  props: React.Attributes & TypographyProps,
  ...children: React.ReactNode[]
): React.ReactElement => React.createElement(Typography, props, ...children)
